import React, { useState, useCallback, useContext, useRef, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useQuery } from 'react-query';
import * as api from '@ubeya/shared/services/api';

import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { Avatar } from '@ubeya/shared-web/components/Avatar';
import Popover from '@ubeya/shared-web/components/Popover/Popover';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import { HELP_DESK_INTERNAL_NOTE_COLORS } from '@ubeya/shared/constants/helpDesk';
import { useHelpDeskAnalytics } from '../helpDeskAnalytics';
import useHelpDeskFileUpload from '../hooks/useHelpDeskFileUpload';
import ComposerAttachmentPreview from './ComposerAttachmentPreview';
import ComposerToolbar from './ComposerToolbar';

// region: Styled components

const ComposerWrapper = styled.div`
  border: 1px solid ${({ theme, $isInternal }) => ($isInternal ? theme.colors.orangeChanges : theme.colors.grey2)};
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.15s, background 0.15s;
  background: ${({ theme }) => theme.colors.white};
  padding: 16px 8px 8px 16px;

  ${({ $isInternal, theme }) =>
    $isInternal &&
    css`
      background: ${HELP_DESK_INTERNAL_NOTE_COLORS.background};
      border-color: ${theme.colors.orangeChanges};
    `}
`;

const TextAreaAnchor = styled.div`
  width: 100%;
`;

const ComposerTextArea = styled.textarea`
  width: 100%;
  height: 20px;
  min-height: 20px;
  max-height: 160px;
  padding: 0;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.black};
  line-height: 20px;
  overflow-y: auto;
  margin-bottom: 12px;
  text-align: left;
  &::placeholder {
    color: ${({ theme, $isInternal }) => ($isInternal ? theme.colors.grey4 : theme.colors.grey3)};
  }
`;

// region: Mentions dropdown

const MentionsList = styled.div`
  width: 260px;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
`;

const MentionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};

  &:hover {
    background: ${({ theme }) => theme.colors.grey1};
  }

  ${({ $highlighted, theme }) =>
    $highlighted &&
    css`
      background: ${theme.colors.hoverBackground};
    `}
`;

const MentionName = styled.span`
  font-weight: 400;
`;

const getAdminNameParts = (admin) => ({
  firstName: admin.firstName || admin.name?.split(' ')[0] || '',
  lastName: admin.lastName || admin.name?.split(' ').slice(1).join(' ') || ''
});

// region: Component

const HelpDeskComposer = ({ accountId, onSend, isSending, ticket }) => {
  const { t } = useContext(ConfigContext);
  const { data: { id: currentAdminId } = {} } = useUser();
  const { trackHelpDeskTicketEvent } = useHelpDeskAnalytics();
  const [text, setText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const {
    pendingFiles,
    uploadingCount,
    handleFileSelect,
    removeFile,
    reset: resetAttachments
  } = useHelpDeskFileUpload({ accountId });
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const textAreaRef = useRef(null);

  // Fetch admins for @mentions
  const branchId = ticket?.branchId;
  const categoryId = ticket?.categoryId;
  const { data: adminsData } = useQuery(
    ['helpDeskAdmins', accountId, branchId, categoryId],
    () => api.fetchHelpDeskAdmins({ accountId, branchId, categoryId }),
    { enabled: !!accountId && !!branchId }
  );

  const admins = useMemo(() => adminsData?.data ?? [], [adminsData?.data]);

  const filteredAdmins = useMemo(() => {
    if (mentionQuery === null || !isInternalNote) return [];
    const q = mentionQuery.toLowerCase();
    return admins.filter((a) => {
      // Don't show yourself in the mentions list
      if (currentAdminId && a.id === currentAdminId) return false;
      const name = a.name || a.firstName || '';
      return name.toLowerCase().includes(q);
    });
  }, [admins, mentionQuery, isInternalNote, currentAdminId]);

  const showMentionDropdown = isInternalNote && mentionQuery !== null && filteredAdmins.length > 0;

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textAreaRef.current;
    if (el) {
      el.style.height = '20px';
      el.style.height = `${Math.min(Math.max(el.scrollHeight, 20), 160)}px`;
    }
  }, []);

  // Mention detection in text (only in internal note mode)
  const handleTextChange = useCallback(
    (e) => {
      const value = e.target.value;
      setText(value);

      // Detect @mention query only in internal note mode
      if (isInternalNote) {
        const cursorPos = e.target.selectionStart;
        const textBeforeCursor = value.slice(0, cursorPos);
        const atMatch = textBeforeCursor.match(/@([\w\s]*)$/);
        if (atMatch) {
          setMentionQuery(atMatch[1]);
          setMentionIndex(0);
        } else {
          setMentionQuery(null);
        }
      } else {
        setMentionQuery(null);
      }

      // Auto-resize
      setTimeout(autoResize, 0);
    },
    [autoResize, isInternalNote]
  );

  const insertMention = useCallback(
    (admin) => {
      const cursorPos = textAreaRef.current?.selectionStart ?? text.length;
      const textBefore = text.slice(0, cursorPos);
      const textAfter = text.slice(cursorPos);
      const atIdx = textBefore.lastIndexOf('@');
      const mentionName = admin.name || admin.firstName || 'Admin';
      const newText = textBefore.slice(0, atIdx) + `@${mentionName} ` + textAfter;
      setText(newText);
      setMentionQuery(null);

      // Refocus textarea
      setTimeout(() => {
        if (textAreaRef.current) {
          const newPos = atIdx + mentionName.length + 2;
          textAreaRef.current.focus();
          textAreaRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);
    },
    [text]
  );

  // Handle mention button click — insert @ and trigger mention autocomplete
  const handleMentionButtonClick = useCallback(() => {
    if (!isInternalNote) return;
    const el = textAreaRef.current;
    if (!el) return;

    const cursorPos = el.selectionStart ?? text.length;
    const textBefore = text.slice(0, cursorPos);
    const textAfter = text.slice(cursorPos);
    // Only add @ if last char is not already @
    const needsAt = !textBefore.endsWith('@');
    const newText = needsAt ? textBefore + '@' + textAfter : text;
    setText(newText);

    if (needsAt) {
      setMentionQuery('');
      setMentionIndex(0);
    }

    // Refocus textarea
    setTimeout(() => {
      if (el) {
        const newPos = cursorPos + (needsAt ? 1 : 0);
        el.focus();
        el.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }, [isInternalNote, text]);

  // Submit
  const hasContent = text.trim().length > 0 || pendingFiles.some((f) => f.uploadedData);
  const isUploading = uploadingCount > 0;

  // Extract @mention admin IDs from text
  const extractMentionedAdminIds = useCallback(
    (inputText) => {
      const ids = [];
      // Sort by name length descending so "John Smith" matches before "John"
      const sorted = (Array.isArray(admins) ? admins : []).filter(Boolean).sort((a, b) => {
        const nameA = a.name || a.firstName || '';
        const nameB = b.name || b.firstName || '';
        return nameB.length - nameA.length;
      });
      let remaining = inputText || '';
      for (const admin of sorted) {
        const name = admin.name || admin.firstName || '';
        if (!name) continue;
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`@${escaped}(?=\\s|$|[^\\w])`, 'g');
        if (regex.test(remaining)) {
          ids.push(admin.id);
          remaining = remaining.replace(regex, '');
        }
      }
      return [...new Set(ids)];
    },
    [admins]
  );

  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (!hasContent || isUploading || isSending) return;

      const pendingFilesList = Array.isArray(pendingFiles) ? pendingFiles : [];
      const uploadedAttachments = pendingFilesList.filter((f) => f?.uploadedData).map((f) => f.uploadedData);
      const hasText = text.trim().length > 0;
      const messageLength = hasText ? text.trim().length : 0;
      const mentionedAdminIds = hasText ? extractMentionedAdminIds(text.trim()) || [] : [];

      // Build a single message with content and/or attachments
      const payload = {
        content: hasText ? text.trim() : ' ',
        isInternalNote,
        ...(uploadedAttachments.length > 0 && { attachments: uploadedAttachments }),
        ...(mentionedAdminIds.length > 0 && { mentionedAdminIds })
      };
      await onSend(payload);

      trackHelpDeskTicketEvent({
        eventName: isInternalNote ? 'Help Desk - Admin Added Internal Note' : 'Help Desk - Admin Sent Message',
        context: 'Help Desk composer',
        ticket,
        eventProperties: {
          hasAttachments: uploadedAttachments.length > 0,
          attachmentsCount: uploadedAttachments.length,
          messageLength
        }
      });

      mentionedAdminIds.forEach((mentionedAdminId) => {
        trackHelpDeskTicketEvent({
          eventName: 'Help Desk - Admin Mentioned Admin',
          context: 'Help Desk composer',
          ticket,
          eventProperties: {
            mentionedAdminId
          }
        });
      });

      setText('');
      resetAttachments();

      // Reset textarea height and refocus
      if (textAreaRef.current) {
        textAreaRef.current.style.height = '20px';
        textAreaRef.current.focus();
      }
    },
    [
      extractMentionedAdminIds,
      hasContent,
      isInternalNote,
      isSending,
      isUploading,
      onSend,
      pendingFiles,
      resetAttachments,
      text,
      ticket,
      trackHelpDeskTicketEvent
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      // Mention dropdown keyboard navigation
      if (showMentionDropdown) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionIndex((i) => Math.min(i + 1, filteredAdmins.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionIndex((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          insertMention(filteredAdmins[mentionIndex]);
          return;
        }
        if (e.key === 'Escape') {
          setMentionQuery(null);
          return;
        }
      }

      // Enter to send (Shift+Enter for new line)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [showMentionDropdown, filteredAdmins, mentionIndex, insertMention, handleSubmit]
  );

  // Clear mention state when switching out of internal note mode
  useEffect(() => {
    if (!isInternalNote) {
      setMentionQuery(null);
    }
  }, [isInternalNote]);

  const placeholder = isInternalNote ? t('addInternalNotePlaceHolder') : t('help_desk_reply_placeholder');

  const mentionsPopoverContent = (
    <MentionsList>
      {filteredAdmins.map((admin, idx) => {
        const { firstName, lastName } = getAdminNameParts(admin);

        return (
          <MentionItem
            key={admin.id}
            $highlighted={idx === mentionIndex}
            onMouseDown={(e) => {
              e.preventDefault();
              insertMention(admin);
            }}>
            <Avatar firstName={firstName} lastName={lastName} image={admin.image} size="xs" />
            <MentionName>{admin.name || admin.firstName}</MentionName>
          </MentionItem>
        );
      })}
    </MentionsList>
  );

  return (
    <ComposerWrapper $isInternal={isInternalNote}>
      <form onSubmit={handleSubmit}>
        <ComposerAttachmentPreview attachments={pendingFiles} onRemove={removeFile} />

        <Popover
          isPopoverOpen={showMentionDropdown}
          onClickOutside={() => setMentionQuery(null)}
          positions={['top', 'bottom']}
          showArrow={false}
          containerStyle={{ zIndex: '9999' }}
          popoverContentStyle={{ borderRadius: 8 }}
          popoverContent={mentionsPopoverContent}
          triggerContent={
            <TextAreaAnchor>
              <ComposerTextArea
                ref={textAreaRef}
                value={text}
                rows={1}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                $isInternal={isInternalNote}
                disabled={isSending}
              />
            </TextAreaAnchor>
          }
        />

        <ComposerToolbar
          isInternalNote={isInternalNote}
          onInternalNoteToggle={setIsInternalNote}
          onFilesSelected={handleFileSelect}
          onMentionClick={handleMentionButtonClick}
          canSend={hasContent && !isUploading}
          isSending={isSending}
        />
      </form>
    </ComposerWrapper>
  );
};

export default HelpDeskComposer;
